import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { Sila3DExplorer } from '@/components/public/Sila3DExplorer';
import { BookingDialog } from '@/components/public/BookingDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Leaf, ArrowRight, ChevronDown, Clock, Ruler, Mountain, CalendarCheck, MapPin, Compass } from 'lucide-react';

const U = 'https://images.unsplash.com/';
const ux = (pid, w = 1400) => `${U}${pid}?auto=format&fit=crop&w=${w}&q=80`;

const HERO = ux('photo-1470071459604-3b5ec3a7fe05', 1920);
const GALLERY = [
  { src: ux('photo-1441974231531-c6227db76b6e', 900), caption: 'Foresta di pini larici · l\u2019alba tra gli alberi', span: 'md:col-span-2 md:row-span-2' },
  { src: ux('photo-1508923567004-3a6b8004f3d7', 700), caption: 'Faggeta · foliage d\u2019autunno' },
  { src: ux('photo-1506905925346-21bda4d32df4', 700), caption: 'Vette · verso il Botte Donato' },
  { src: ux('photo-1439066615861-d1af74d74000', 700), caption: 'Riflessi · Lago Cecita' },
  { src: ux('photo-1484406566174-9da000fda645', 700), caption: 'Fauna · il cervo della Sila' },
];

// IntersectionObserver: quale step \u00e8 attivo
const useActiveStep = (count) => {
  const [active, setActive] = useState(0);
  const refs = useRef([]);
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const i = Number(e.target.getAttribute('data-index'));
            if (!Number.isNaN(i)) setActive(i);
          }
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    refs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [count]);
  const setRef = useCallback((el, i) => { refs.current[i] = el; }, []);
  return { active, setRef };
};

const Meta = ({ area }) => (
  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-muted-foreground">
    {area.duration ? <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{area.duration}</span> : null}
    {area.length_km ? <span className="inline-flex items-center gap-1.5"><Ruler className="h-3.5 w-3.5" />{area.length_km} km</span> : null}
    {area.difficulty ? <span className="inline-flex items-center gap-1.5"><Mountain className="h-3.5 w-3.5" />{area.difficulty}</span> : null}
  </div>
);

export default function ParchiImmersive() {
  const [areas, setAreas] = useState([]);
  const [booking, setBooking] = useState({ open: false, area: null });
  const mapWrapRef = useRef(null);

  useEffect(() => {
    api.get('/parco/public/areas').then(({ data }) => setAreas(data || [])).catch(() => {});
  }, []);

  const { active, setRef } = useActiveStep(areas.length);
  const activeArea = areas[active];

  const scrollToMap = () => mapWrapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  return (
    <PublicLayout>
      <Seo title="Parco Nazionale della Sila \u2014 Esperienza immersiva" description="Vola tra gli itinerari della Sila in 3D: boschi di pini larici, laghi cristallini e vette panoramiche. Scopri e prenota i tour guidati." path="/parchi-preview" image={HERO} />

      {/* ============ HERO CINEMATOGRAFICO ============ */}
      <section className="sila-hero-scrim relative isolate flex min-h-[92vh] items-end overflow-hidden text-white" data-testid="parchi-hero">
        <img src={HERO} alt="Parco Nazionale della Sila" className="sila-kb absolute inset-0 -z-10 h-full w-full object-cover" />
        <div aria-hidden className="grain-overlay absolute inset-0 -z-10 opacity-40" />
        <div className="sila-section w-full pb-20 pt-40">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] backdrop-blur-md sm:text-xs">
            <Leaf className="h-3.5 w-3.5" style={{ color: 'hsl(var(--brand-sun))' }} />Parco Nazionale della Sila
          </span>
          <h1 className="mt-5 max-w-4xl font-display text-5xl font-bold leading-[1.02] tracking-tight drop-shadow-[0_2px_24px_rgba(0,0,0,0.5)] sm:text-6xl lg:text-7xl">
            Dove la foresta<br />tocca il cielo
          </h1>
          <p className="mt-5 max-w-xl text-lg text-white/85 sm:text-xl">
            Un altopiano di pini secolari, laghi color smeraldo e silenzi profondi. Scorri e vola tra gli itinerari delle nostre guide.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" className="rounded-full" onClick={scrollToMap} data-testid="parchi-hero-primary-cta">
              Esplora gli itinerari<ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-white/10 text-white backdrop-blur-md hover:bg-white/20 hover:text-white" data-testid="parchi-hero-secondary-cta">
              <Link to="/parco-3d">Mappa 3D a schermo intero</Link>
            </Button>
          </div>
          <div className="sila-bob mt-12 inline-flex items-center gap-2 text-sm text-white/70">
            <ChevronDown className="h-5 w-5" /> Scorri per iniziare il viaggio
          </div>
        </div>
      </section>

      {/* ============ SCROLLYTELLING: MAPPA STICKY + STEP ============ */}
      <section ref={mapWrapRef} className="sila-section py-16 sm:py-24" data-testid="parchi-scrollytelling">
        <div className="mb-10 max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--brand-sun)/0.22)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--brand-bark))]"><Compass className="h-3.5 w-3.5" />Sila 3D Explorer</span>
          <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">Gli itinerari, uno ad uno</h2>
          <p className="mt-3 text-lg text-muted-foreground">Mentre scorri, la mappa vola verso ogni percorso. Apri i punti panoramici e prenota il tuo tour guidato.</p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Mappa sticky (desktop) */}
          <div className="lg:col-span-7">
            <div className="lg:sticky lg:top-20" data-testid="parchi-sticky-map">
              <div className="relative">
                <Sila3DExplorer areas={areas} focusId={activeArea?.id} showPanel={false} showToggle onBook={(area) => setBooking({ open: true, area })} />
                {/* Etichetta capitolo (glass) */}
                {activeArea && (
                  <div key={activeArea.id} className="sila-glass pointer-events-none absolute bottom-4 left-4 z-[5] max-w-[280px] rounded-[var(--radius-lg)] p-3">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: activeArea.color || 'hsl(var(--primary))' }} />
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Capitolo {active + 1} / {areas.length}</span>
                    </div>
                    <p className="mt-1 font-display text-lg font-semibold leading-tight text-foreground">{activeArea.name}</p>
                  </div>
                )}
                {/* Progresso a dots */}
                <div className="absolute right-4 top-4 z-[5] hidden flex-col items-center gap-2 rounded-full bg-background/70 px-2 py-3 backdrop-blur lg:flex">
                  {areas.map((a, i) => (
                    <span key={a.id} className="sila-step-dot" data-active={i === active} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Narrative rail */}
          <div className="lg:col-span-5">
            <div className="flex flex-col gap-6 lg:gap-0">
              {areas.map((area, i) => (
                <article
                  key={area.id}
                  ref={(el) => setRef(el, i)}
                  data-index={i}
                  data-testid="parchi-story-step"
                  className="lg:flex lg:min-h-[80vh] lg:flex-col lg:justify-center"
                >
                  <div
                    className={`sila-glass overflow-hidden rounded-[var(--radius-lg)] transition-[transform,box-shadow,border-color] duration-300 ${i === active ? 'lg:-translate-y-0.5 ring-2 ring-[hsl(var(--brand-sun)/0.55)]' : 'lg:opacity-80'}`}
                    data-testid="itinerary-story-card"
                  >
                    <div className="sila-photo-scrim relative h-56 w-full overflow-hidden sm:h-64">
                      {area.cover_image ? <img src={area.cover_image} alt={area.name} className="h-full w-full object-cover" /> : null}
                      {area.difficulty ? <Badge className="absolute left-4 top-4 z-[1] rounded-full bg-background/85 text-foreground backdrop-blur" data-testid="itinerary-difficulty-badge">{area.difficulty}</Badge> : null}
                      <div className="absolute inset-x-0 bottom-0 z-[1] p-5 text-white">
                        {area.subtitle ? <span className="inline-flex items-center gap-1 text-xs font-medium text-white/85"><MapPin className="h-3 w-3" />{area.subtitle}</span> : null}
                        <h3 className="mt-1 font-display text-2xl font-bold leading-tight drop-shadow">{area.name}</h3>
                      </div>
                    </div>
                    <div className="p-5">
                      <p className="text-sm leading-relaxed text-muted-foreground">{(area.description || '').slice(0, 180)}{(area.description || '').length > 180 ? '\u2026' : ''}</p>
                      <Meta area={area} />
                      <div className="mt-4 flex items-center justify-between">
                        {area.price != null ? (
                          <span className="text-lg font-semibold text-foreground" data-testid="itinerary-price-text">€{area.price}<span className="text-sm font-normal text-muted-foreground">/persona</span></span>
                        ) : <span />}
                        <Button className="rounded-full" onClick={() => setBooking({ open: true, area })} data-testid="itinerary-book-button">
                          <CalendarCheck className="mr-2 h-4 w-4" />Prenota
                        </Button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>

        {areas.length === 0 && (
          <p className="mt-6 rounded-xl border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground" data-testid="parchi-empty">Nessun itinerario pubblicato al momento. Torna presto: le guide stanno tracciando i primi percorsi.</p>
        )}
      </section>

      {/* ============ GALLERIA TERRITORIO (BENTO) ============ */}
      <section className="sila-section pb-24">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Il respiro della Sila</h2>
            <p className="mt-2 text-muted-foreground">Frammenti di un territorio che cambia con le stagioni.</p>
          </div>
        </div>
        <div className="grid auto-rows-[180px] grid-cols-2 gap-4 sm:auto-rows-[220px] md:grid-cols-4">
          {GALLERY.map((g, i) => (
            <figure key={i} data-testid="territory-gallery-item" className={`sila-photo-scrim group relative overflow-hidden rounded-[var(--radius-lg)] ${g.span || ''}`}>
              <img loading="lazy" src={g.src} alt={g.caption} className="h-full w-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110" />
              <figcaption className="absolute inset-x-0 bottom-0 z-[1] p-4 text-sm font-medium text-white drop-shadow">{g.caption}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ============ CTA PRENOTA ============ */}
      <section className="sila-section pb-28">
        <div className="grid overflow-hidden rounded-[28px] border border-border bg-card shadow-[var(--shadow-soft)] md:grid-cols-2">
          <div className="sila-photo-scrim relative min-h-[240px]">
            <img src={ux('photo-1441974231531-c6227db76b6e', 1000)} alt="Guida escursionistica nella Sila" className="absolute inset-0 h-full w-full object-cover" />
          </div>
          <div className="flex flex-col justify-center gap-4 p-8 sm:p-12">
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Vivi la Sila con una guida del posto</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary" />Guide locali certificate ed esperte del territorio</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary" />Itinerari per famiglie, coppie e appassionati</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary" />Prenotazione semplice, conferma diretta</li>
            </ul>
            <div className="mt-2 flex flex-wrap gap-3">
              <Button size="lg" className="rounded-full" onClick={() => setBooking({ open: true, area: areas[0] || null })} data-testid="parchi-cta-book">
                Prenota un tour<ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full">
                <Link to="/partner/accedi">Sei una guida? Accedi</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <BookingDialog open={booking.open} onOpenChange={(o) => setBooking((b) => ({ ...b, open: o }))} area={booking.area} />
    </PublicLayout>
  );
}
